import { getPackageUniqueKey } from 'lib/offer/offerUtils'
import { getMaxOccupancies, getOccupanciesFromReservations } from 'lib/offer/occupancyUtils'
import { orderItemHasReservationChannelManager } from './orderItemUtils'

/**
 * Availability for a stay the guest already holds excludes their own room, because their
 * reservation counts against inventory. svc-reservation only adds that room back when the
 * existing reservation id is supplied (the `+1` allowance added in OPEX-2284), so without it
 * a sold-out room type returns no rate for the very package they own — and `canAddExtraGuests`
 * fails its `availableRates` lookup. Extend-dates wired this up in CSX-2284; the extra guest
 * flows did not.
 *
 * Only self-managed inventory is served from svc-reservation's own tables. External channel
 * managers answer availability from the connection, where the id has no effect, so it is
 * left off for them.
 */
export function getExtraGuestsAvailabilityReservationId(item: App.OrderItem | undefined): string | undefined {
  return orderItemHasReservationChannelManager(item, 'self-managed') ? item?.reservation?.id : undefined
}

export function getExtraGuestCapacity(offer: App.HotelOffer, item: App.OrderItem, packageKey: string): {
  adults: number
  children: number
  infants: number
  teenagers: number
} | null {
  if (!offer) return null
  if (!item?.reservation) return null

  const pkg = offer.packages.find(p => p.uniqueKey === packageKey)

  const occupancy = getOccupanciesFromReservations(item.reservation)

  return getMaxOccupancies(pkg?.roomRate.capacities, occupancy, {
    maxChildAge: offer.property?.maxChildAge,
    maxInfantAge: offer.property?.maxInfantAge,
    maxTeenagerAge: offer.property?.maxTeenagerAge,
  })
}

interface canAddExtraGuestsOptions {
  ignoreAvailableRates?: boolean
  canAddExtraGuests?: boolean
}

// Booking policy belongs to svc-order. The portal only checks the live rate
// availability that is not present in the order's eligibility response.
export function canAddExtraGuests(item: App.OrderItem | undefined, availableRates: Array<App.OfferAvailableRate>, options: canAddExtraGuestsOptions = {}): boolean {
  if (options.canAddExtraGuests !== true || !item?.reservation) return false
  if (options.ignoreAvailableRates) return true
  const itemPackage = item.package as App.OrderItemHotelPackage
  const key = getPackageUniqueKey(itemPackage.id, itemPackage.duration, itemPackage.roomRateId)
  return availableRates.some(rate => rate.packageUniqueKey === key)
}
